/**
 * Daily Collections print screen  (طباعة التحصيلات باليوم)
 *
 * A clean, printable A4 sheet listing every collection (payment) the current
 * rep recorded today, with a grand total. Managers can pass ?salesman_uid to
 * print a specific rep, or print everyone by default.
 *
 * Route: /collections/print/today
 *
 * This is a NEW screen — it does not modify any existing page. It reuses the
 * already-scoped `sellgo_coll_history` endpoint (date_from = date_to = today).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Printer, ArrowRight, Share2, FileText, Receipt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import LoadingState from '@/components/shared/LoadingState';
import ErrorState from '@/components/shared/ErrorState';
import { api } from '@/api/apiClient';
import { useOdooAuth } from '@/lib/OdooAuthContext';
import { shareElementAsImage } from '@/lib/shareDocument';
import { useCompanyInfo } from '@/lib/useCompanyInfo';
import { cur } from '@/lib/currency';

function todayLocalISO() {
  const d = new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
}

const fmt = (n) =>
  Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const lbl = (pair, fallback = '—') =>
  Array.isArray(pair) && pair.length > 1 ? pair[1] : fallback;

export default function DailyCollectionsPrint() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { odooSession } = useOdooAuth();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [format, setFormat] = useState('a4'); // 'a4' | 'thermal'
  const printRef = useRef(null);
  const scrollRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const company = useCompanyInfo();

  const today = todayLocalISO();
  const salesmanUid = searchParams.get('salesman_uid') || undefined;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { date_from: today, date_to: today, limit: 200 };
      if (salesmanUid) params.salesman_uid = salesmanUid;
      let res;
      try {
        res = await api.get('/sellgo_coll_history', params);
      } catch {
        res = await api.get('/collections/payments', params);
      }
      if (res?.success) setRows(res.data || []);
      else setError(res?.error || 'فشل تحميل التحصيلات');
    } catch (e) {
      setError(e?.message || 'فشل تحميل التحصيلات');
    } finally {
      setLoading(false);
    }
  }, [today, salesmanUid]);

  useEffect(() => { load(); }, [load]);

  // Switch paper size for printing (A4 vs thermal 80mm).
  useEffect(() => {
    document.documentElement.setAttribute('data-print-format', format);
    return () => document.documentElement.removeAttribute('data-print-format');
  }, [format]);

  // On-screen A4 preview: scale the full 800px sheet down to fit the screen
  // width so it looks like a real (smaller) A4 page instead of a cramped table.
  // Thermal mode is already narrow, so it stays at zoom 1. Print & share are
  // unaffected (print resets zoom via CSS; share captures at fixed width).
  useEffect(() => {
    if (format === 'thermal') { setZoom(1); return; }
    const el = scrollRef.current;
    if (!el) return;
    const compute = () => setZoom(Math.min(1, (el.clientWidth - 24) / 800));
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [format]);

  const handleShare = async () => {
    if (!printRef.current) return;
    const t = toast.loading('جاري تجهيز الصورة...');
    try {
      const res = await shareElementAsImage(printRef.current, {
        filename: 'تحصيلات-اليوم.jpg', title: 'تحصيلات اليوم',
        captureWidth: format === 'thermal' ? undefined : 800,
      });
      toast.dismiss(t);
      if (res === 'downloaded') toast.success('تم حفظ الصورة');
    } catch {
      toast.dismiss(t);
      toast.error('تعذّر تجهيز الصورة');
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);
  const dateLabel = new Date(today).toLocaleDateString('ar-EG', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  const isThermal = format === 'thermal';

  return (
    <div className="flex flex-col min-h-screen bg-muted/30" dir="rtl">
      {/* Toolbar (hidden when printing) */}
      <div className="no-print sticky top-0 z-20 flex items-center gap-3 bg-card border-b px-4 py-3 shadow-sm">
        <button onClick={() => navigate(-1)} className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-muted">
          <ArrowRight className="w-4 h-4 text-muted-foreground" />
        </button>
        <h1 className="flex-1 text-lg font-bold text-foreground truncate">تحصيلات اليوم</h1>
        <div className="flex bg-muted rounded-xl p-1">
          <button onClick={() => setFormat('a4')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${format === 'a4' ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground'}`}>
            <FileText className="w-3.5 h-3.5" /> A4
          </button>
          <button onClick={() => setFormat('thermal')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${format === 'thermal' ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground'}`}>
            <Receipt className="w-3.5 h-3.5" /> حراري 80مم
          </button>
        </div>
      </div>

      {/* The printable sheet */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto pb-28">
        <div
          ref={printRef}
          className={`print-area report-doc ${isThermal ? 'thermal' : ''} mx-auto my-6 bg-white rounded-lg shadow-sm text-[#1f2937] ${isThermal ? 'max-w-[340px] p-4' : 'p-8'}`}
          style={isThermal ? undefined : { width: 800, zoom }}
          dir="rtl"
        >
          <div className="flex items-start justify-between border-b pb-4 mb-5 flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight">تحصيلات اليوم</h1>
              <p className="text-sm text-gray-500 mt-1">{dateLabel}</p>
            </div>
            <div className="text-left flex items-center gap-2">
              {company.logoUrl && <img src={company.logoUrl} alt="" className="w-12 h-12 rounded-lg object-contain flex-shrink-0" />}
              <div>
                <div className="text-lg font-bold leading-tight">{company.name}</div>
                <div className="text-xs text-gray-400">{[company.address.city, company.address.country].filter(Boolean).join('، ')}</div>
              </div>
            </div>
          </div>

          <div className="flex justify-between text-sm mb-4 gap-2 flex-wrap">
            <span className="text-gray-500">المندوب: <span className="font-semibold text-gray-800">{odooSession?.name || '—'}</span></span>
            <span className="text-gray-500">عدد السندات: <span className="font-semibold text-gray-800">{rows.length}</span></span>
          </div>

          {rows.length === 0 ? (
            <p className="text-center text-gray-400 py-12">لا توجد تحصيلات مسجّلة اليوم.</p>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 text-gray-600">
                  <th className="text-right font-semibold py-2 px-2 border-b">#</th>
                  <th className="text-right font-semibold py-2 px-2 border-b">العميل</th>
                  <th className="a4-only text-right font-semibold py-2 px-2 border-b">رقم السند</th>
                  <th className="a4-only text-right font-semibold py-2 px-2 border-b">الفواتير المسدّدة</th>
                  <th className="a4-only text-right font-semibold py-2 px-2 border-b">طريقة الدفع</th>
                  <th className="text-left font-semibold py-2 px-2 border-b">المبلغ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} className="border-b last:border-0 align-top">
                    <td className="py-2 px-2 text-gray-400">{i + 1}</td>
                    <td className="py-2 px-2 font-medium">{lbl(r.partner_id)}</td>
                    <td className="a4-only py-2 px-2 text-gray-600 break-all">{r.name || '—'}</td>
                    <td className="a4-only py-2 px-2 text-gray-600">
                      {Array.isArray(r.invoices) && r.invoices.length > 0 ? (
                        <div className="space-y-0.5">
                          {r.invoices.map((inv) => (
                            <div key={inv.id} className="flex justify-between gap-3">
                              <span className="font-mono text-xs break-all">{inv.name}</span>
                              <span className="text-emerald-700 font-semibold">{fmt(inv.amount)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-gray-400">دفعة مقدمة</span>
                      )}
                    </td>
                    <td className="a4-only py-2 px-2 text-gray-600">{lbl(r.payment_method_line_id, lbl(r.journal_id))}</td>
                    <td className="py-2 px-2 text-left font-semibold text-emerald-700">{fmt(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-800">
                  <td colSpan={isThermal ? 2 : 5} className="py-3 px-2 text-right font-bold">الإجمالي</td>
                  <td className="py-3 px-2 text-left font-extrabold text-emerald-700">{fmt(total)} {cur('ج.م')}</td>
                </tr>
              </tfoot>
            </table>
          )}

          <div className="mt-10 flex justify-between text-xs text-gray-400 gap-2">
            <span>توقيع المندوب: ____________________</span>
            <span className="a4-only">توقيع المستلم: ____________________</span>
          </div>
        </div>
      </div>

      {/* Action bar */}
      <div className="no-print fixed bottom-0 right-0 left-0 bg-card border-t border-border px-4 py-3 z-30 flex gap-2">
        <Button onClick={handleShare} variant="outline" className="flex-1 rounded-xl h-12 gap-2">
          <Share2 className="w-4 h-4" /> مشاركة
        </Button>
        <Button onClick={() => window.print()} className="flex-1 bg-primary rounded-xl h-12 gap-2">
          <Printer className="w-4 h-4" /> طباعة / حفظ PDF
        </Button>
      </div>
    </div>
  );
}
